<template>
  <div class="space-y-6">
    <header class="flex items-center justify-between">
      <div>
        <h1 class="text-2xl font-semibold text-white">Warehouse Address</h1>
        <p class="text-sm text-slate-400 mt-1">
          Configure the ship-from origin used for all outbound &amp; return labels.
        </p>
      </div>
      <div class="flex items-center gap-3">
        <button
          class="px-4 py-2 rounded-lg border border-slate-600 text-slate-300 hover:border-slate-400 hover:text-white transition-colors text-sm"
          :disabled="validating || !form.line1 || !form.city"
          @click="validateAddress"
        >
          {{ validating ? 'Validating…' : 'Validate Address' }}
        </button>
        <button
          class="btn-primary"
          :disabled="saving || !isDirty"
          @click="saveAddress"
        >
          {{ saving ? 'Saving…' : 'Save Changes' }}
        </button>
      </div>
    </header>

    <section class="panel-neo panel-neo--glassier p-6 rounded-2xl max-w-2xl">
      <div v-if="loading" class="py-24 text-center text-slate-400">Loading warehouse address…</div>
      <div v-else-if="loadError" class="py-24 text-center text-rose-400">{{ loadError }}</div>
      <div v-else class="space-y-5">
        <!-- Company info row -->
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-xs uppercase tracking-[0.15em] text-slate-400 mb-1">Contact Name</label>
            <input
              v-model="form.contact_name"
              type="text"
              placeholder="e.g. Routr Warehouse"
              class="input-dark w-full"
            />
          </div>
          <div>
            <label class="block text-xs uppercase tracking-[0.15em] text-slate-400 mb-1">Company</label>
            <input
              v-model="form.company"
              type="text"
              placeholder="e.g. Routr"
              class="input-dark w-full"
            />
          </div>
        </div>

        <!-- Address -->
        <div>
          <label class="block text-xs uppercase tracking-[0.15em] text-slate-400 mb-1">Address Line 1 <span class="text-rose-400">*</span></label>
          <input
            v-model="form.line1"
            type="text"
            placeholder="Street address"
            class="input-dark w-full"
            required
          />
        </div>
        <div>
          <label class="block text-xs uppercase tracking-[0.15em] text-slate-400 mb-1">Address Line 2</label>
          <input
            v-model="form.line2"
            type="text"
            placeholder="Suite, unit, floor"
            class="input-dark w-full"
          />
        </div>

        <!-- City / State / ZIP row -->
        <div class="grid grid-cols-[2fr_1fr_1fr] gap-4">
          <div>
            <label class="block text-xs uppercase tracking-[0.15em] text-slate-400 mb-1">City <span class="text-rose-400">*</span></label>
            <input
              v-model="form.city"
              type="text"
              placeholder="City"
              class="input-dark w-full"
              required
            />
          </div>
          <div>
            <label class="block text-xs uppercase tracking-[0.15em] text-slate-400 mb-1">State <span class="text-rose-400">*</span></label>
            <input
              v-model="form.state"
              type="text"
              maxlength="2"
              placeholder="CA"
              class="input-dark w-full uppercase"
              required
            />
          </div>
          <div>
            <label class="block text-xs uppercase tracking-[0.15em] text-slate-400 mb-1">ZIP <span class="text-rose-400">*</span></label>
            <input
              v-model="form.postal_code"
              type="text"
              maxlength="10"
              placeholder="94105"
              class="input-dark w-full"
              required
            />
          </div>
        </div>

        <!-- Country -->
        <div class="w-48">
          <label class="block text-xs uppercase tracking-[0.15em] text-slate-400 mb-1">Country</label>
          <select v-model="form.country" class="input-dark w-full">
            <option value="US">United States</option>
            <option value="CA">Canada</option>
          </select>
        </div>

        <!-- Phone / Email row -->
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-xs uppercase tracking-[0.15em] text-slate-400 mb-1">Phone</label>
            <input
              v-model="form.phone"
              type="tel"
              placeholder="(555) 123-4567"
              class="input-dark w-full"
            />
          </div>
          <div>
            <label class="block text-xs uppercase tracking-[0.15em] text-slate-400 mb-1">Email</label>
            <input
              v-model="form.email"
              type="email"
              placeholder="warehouse@company.com"
              class="input-dark w-full"
            />
          </div>
        </div>

        <!-- Address validation results -->
        <div v-if="validationResult" class="p-3 rounded-lg border text-sm space-y-2" :class="validationResult.valid ? 'border-emerald-600/50 bg-emerald-950/30' : 'border-rose-600/50 bg-rose-950/30'">
          <div class="flex items-center gap-2">
            <span v-if="validationResult.valid" class="text-emerald-400 font-medium">✓ Address is valid</span>
            <span v-else class="text-rose-400 font-medium">✗ Address validation failed</span>
          </div>
          <div v-if="validationResult.suggested && validationResult.valid" class="text-xs text-slate-400">
            <span class="text-slate-300">Shippo canonical:</span>
            {{ validationResult.suggested.line1 }},
            {{ validationResult.suggested.city }},
            {{ validationResult.suggested.state }}
            {{ validationResult.suggested.postal_code }}
            <button
              v-if="hasSuggestedDiff"
              class="ml-2 text-primary-300 underline hover:text-primary-200"
              @click="applySuggested"
            >
              Apply suggestion
            </button>
          </div>
          <div v-for="(msg, i) in validationResult.messages" :key="i" class="text-xs" :class="msg.type === 'error' ? 'text-rose-400' : 'text-amber-400/80'">
            {{ msg.source }}: {{ msg.text }}
          </div>
        </div>

        <!-- Inline feedback -->
        <div v-if="saveError" class="text-sm text-rose-400">{{ saveError }}</div>
        <div v-if="saveSuccess" class="text-sm text-emerald-400">{{ saveSuccess }}</div>
      </div>
    </section>

    <!-- Info callout -->
    <section class="panel-neo panel-neo--glassier p-4 rounded-2xl max-w-2xl border border-slate-700/40">
      <p class="text-xs text-slate-400 leading-relaxed">
        <span class="text-slate-300 font-medium">How this works:</span>
        This address is used as the <span class="text-white">ship-from origin</span> on every
        outbound and return shipping label generated through the Shipping Label modal.
        When a return label is created, the borrower ships <em>back to</em> this address.
        Changes take effect immediately for all new labels.
      </p>
    </section>
  </div>
</template>

<script setup lang="ts">
/**
 * AdminWarehouseAddress.vue
 *
 * Admin-only settings page for managing the warehouse ship-from address.
 * Reads/writes to app.settings → warehouse_address via appApi helpers.
 */
import { ref, reactive, computed, onMounted, watch } from 'vue'
import {
  getWarehouseAddress,
  updateSetting,
  validateShippingAddress,
  type WarehouseAddress,
  type ShippingAddress,
} from '@/lib/appApi'

const loading = ref(true)
const loadError = ref('')
const saving = ref(false)
const saveError = ref('')
const saveSuccess = ref('')
const validating = ref(false)
const validationResult = ref<{
  valid: boolean
  messages: Array<{ source: string; code: string; text: string; type: string }>
  suggested: ShippingAddress | null
} | null>(null)

/** Form state — mutable copy of the persisted address */
const form = reactive<WarehouseAddress>({
  contact_name: '',
  company: '',
  line1: '',
  line2: '',
  city: '',
  state: '',
  postal_code: '',
  country: 'US',
  phone: '',
  email: '',
})

/** Snapshot of the last-saved state, used for dirty detection */
const saved = ref('')

const isDirty = computed(() => JSON.stringify(form) !== saved.value)

/**
 * Load the current warehouse address from app.settings.
 */
async function loadAddress() {
  loading.value = true
  loadError.value = ''
  try {
    const addr = await getWarehouseAddress()
    Object.assign(form, addr)
    saved.value = JSON.stringify(form)
  } catch (e: any) {
    loadError.value = e?.message || 'Failed to load warehouse address'
  } finally {
    loading.value = false
  }
}

/**
 * Persist the current form values to app.settings → warehouse_address.
 */
async function saveAddress() {
  if (!form.line1 || !form.city || !form.state || !form.postal_code) {
    saveError.value = 'Address, city, state, and ZIP are required.'
    return
  }
  saving.value = true
  saveError.value = ''
  saveSuccess.value = ''
  try {
    await updateSetting('warehouse_address', { ...form })
    saved.value = JSON.stringify(form)
    saveSuccess.value = 'Warehouse address saved successfully.'
    setTimeout(() => { saveSuccess.value = '' }, 4000)
  } catch (e: any) {
    saveError.value = e?.message || 'Failed to save. Are you an admin?'
  } finally {
    saving.value = false
  }
}

/**
 * Sanitize city field — strip trailing state abbreviations (e.g. "Southfield, MI" → "Southfield").
 * UPS strictly validates address fields and rejects malformed city names.
 */
watch(() => form.city, (val) => {
  if (!val) return
  // Match patterns like "City, ST" or "City ST" at the end
  const match = val.match(/^(.+?)[,\s]+([A-Z]{2})$/i)
  if (match && match[1].trim().length > 1) {
    form.city = match[1].trim()
    // If state is empty, auto-fill it from the trailing abbreviation
    if (!form.state) {
      form.state = match[2].toUpperCase()
    }
  }
})

/**
 * Validate the current address using Shippo's address validation API.
 */
async function validateAddress() {
  validating.value = true
  validationResult.value = null
  saveError.value = ''
  try {
    const res = await validateShippingAddress({
      contact_name: form.contact_name || undefined,
      company: form.company || undefined,
      line1: form.line1,
      line2: form.line2 || undefined,
      city: form.city,
      state: form.state || undefined,
      postal_code: form.postal_code || undefined,
      country: form.country || 'US',
      phone: form.phone || undefined,
      email: form.email || undefined,
    })
    validationResult.value = {
      valid: res.valid,
      messages: res.messages || [],
      suggested: res.suggested || null,
    }
  } catch (e: any) {
    saveError.value = `Validation failed: ${e?.message || String(e)}`
  } finally {
    validating.value = false
  }
}

/**
 * Whether the suggested address differs from the current form values.
 */
const hasSuggestedDiff = computed(() => {
  const s = validationResult.value?.suggested
  if (!s) return false
  return (
    s.line1 !== form.line1 ||
    s.city !== form.city ||
    s.state !== form.state ||
    s.postal_code !== form.postal_code
  )
})

/**
 * Apply the Shippo-suggested address corrections to the form.
 */
function applySuggested() {
  const s = validationResult.value?.suggested
  if (!s) return
  if (s.line1) form.line1 = s.line1
  if (s.line2 !== undefined) form.line2 = s.line2
  if (s.city) form.city = s.city
  if (s.state) form.state = s.state
  if (s.postal_code) form.postal_code = s.postal_code
}

onMounted(loadAddress)
</script>
